/*
 * Sticky cart summary bar.
 *
 * Owns no cart logic of its own. It listens for the theme's cart-update event
 * and re-requests its own section markup, so whatever the cart drawer,
 * quick-add or a third-party app does to the cart is reflected here without
 * this file needing to know about any of it.
 */
class BppStickyCart extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.syncHeight = this.syncHeight.bind(this);
  }

  connectedCallback() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    }

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.syncHeight);
      this.observer.observe(this);
    } else {
      window.addEventListener('resize', this.syncHeight);
    }

    this.syncHeight();
    // Reveal after first paint so the bar slides up rather than snapping in.
    requestAnimationFrame(() => this.classList.add('bpp-sticky-cart--ready'));
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.syncHeight);
    document.body.classList.remove('bpp-has-sticky-bar');
    document.documentElement.style.removeProperty('--bpp-sticky-bar-h');
  }

  syncHeight() {
    const visible = this.dataset.visible === 'true';
    const height = visible ? this.offsetHeight : 0;
    document.documentElement.style.setProperty('--bpp-sticky-bar-h', `${height}px`);
    document.body.classList.toggle('bpp-has-sticky-bar', visible);
  }

  async onCartUpdate() {
    // Two quick adds start two overlapping section fetches. If the first reply
    // lands last it paints the older cart over the newer one, and the bar shows
    // one item while the cart holds two. Only the newest request may write.
    const token = (this.refreshToken = (this.refreshToken || 0) + 1);

    try {
      const url = `${window.location.pathname}?section_id=${this.sectionId}`;
      const response = await fetch(url);
      if (!response.ok || token !== this.refreshToken) return;

      const markup = await response.text();
      if (token !== this.refreshToken) return;

      const fresh = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('bpp-sticky-cart');
      if (!fresh) return;

      this.innerHTML = fresh.innerHTML;
      this.dataset.visible = fresh.dataset.visible;
      this.hidden = fresh.dataset.visible !== 'true';
      this.syncHeight();
    } catch (error) {
      // A failed refresh must never break the page — the cart drawer is still
      // the source of truth, so leave the stale bar in place.
      console.error('Sticky cart refresh failed', error);
    }
  }
}

if (!customElements.get('bpp-sticky-cart')) {
  customElements.define('bpp-sticky-cart', BppStickyCart);
}
